// v2.71.11 — Schema-driven Plan Sections Editor
// Renders the full structured editor for a plan's sections + items.
// Used inside the AdminUnifiedPlansTab dialog. Anonymous of any prior textarea
// state — talks directly to the new sections/items CRUD endpoints.

import React, { useEffect, useState, useCallback } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, X, Pencil, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const SECTION_TYPES = [
  { value: "features",    label: "Features",    accent: "bg-[#DBEAFE] text-[#1E40AF]" },
  { value: "benefits",    label: "Benefits",    accent: "bg-[#DCFCE7] text-[#15803D]" },
  { value: "limitations", label: "Limitations", accent: "bg-[#FEE2E2] text-[#991B1B]" },
  { value: "process",     label: "Process",     accent: "bg-[#FEF3C7] text-[#92400E]" },
  { value: "support",     label: "Support",     accent: "bg-[#EDE9FE] text-[#5B21B6]" },
  { value: "custom",      label: "Custom",      accent: "bg-[#F4F4F5] text-[#52525B]" },
];

const typeMeta = (t) => SECTION_TYPES.find((x) => x.value === t) || SECTION_TYPES[5];

export default function PlanSectionsEditor({ planId }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    try {
      // Reuse the admin list endpoint and pluck this plan's sections
      const r = await api.get("/admin/unified-plans");
      const all = [...(r.data.client || []), ...(r.data.dealer || [])];
      const me = all.find((p) => p.id === planId);
      setSections(me?.sections || []);
    } catch (e) { toast.error(apiErrorMsg(e, "Failed to load sections")); }
    setLoading(false);
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const addSection = async () => {
    try {
      const r = await api.post(`/admin/plans/${planId}/sections`, {
        type: "custom",
        title: "New Section",
        enabled: true,
      });
      setSections((arr) => [...arr, { ...r.data, items: [] }]);
      toast.success("Section added");
    } catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };

  const updateSection = async (id, patch, optimistic = true) => {
    if (optimistic) setSections((arr) => arr.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    try {
      const r = await api.patch(`/admin/plans/sections/${id}`, patch);
      setSections((arr) => arr.map((s) => (s.id === id ? { ...s, ...r.data, items: s.items || [] } : s)));
    } catch (e) {
      toast.error(apiErrorMsg(e, "Update failed"));
      load();
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm("Delete this section and all its items?")) return;
    try {
      await api.delete(`/admin/plans/sections/${id}`);
      setSections((arr) => arr.filter((s) => s.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(apiErrorMsg(e, "Delete failed")); }
  };

  const moveSection = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const a = sections[idx];
    const b = sections[j];
    const next = sections.slice();
    [next[idx], next[j]] = [b, a];
    setSections(next);
    try {
      await Promise.all([
        api.patch(`/admin/plans/sections/${a.id}`, { sort_order: b.sort_order }),
        api.patch(`/admin/plans/sections/${b.id}`, { sort_order: a.sort_order }),
      ]);
    } catch (e) { toast.error("Reorder failed"); load(); }
  };

  const addItem = async (sectionId) => {
    try {
      const r = await api.post(`/admin/plans/sections/${sectionId}/items`, { text: "New item", icon: "" });
      setSections((arr) => arr.map((s) => (s.id === sectionId ? { ...s, items: [...(s.items || []), r.data] } : s)));
    } catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };

  const updateItem = async (sectionId, itemId, patch, optimistic = true) => {
    if (optimistic) {
      setSections((arr) =>
        arr.map((s) =>
          s.id === sectionId
            ? { ...s, items: (s.items || []).map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }
            : s
        )
      );
    }
    try {
      const r = await api.patch(`/admin/plans/items/${itemId}`, patch);
      setSections((arr) =>
        arr.map((s) =>
          s.id === sectionId
            ? { ...s, items: (s.items || []).map((it) => (it.id === itemId ? { ...it, ...r.data } : it)) }
            : s
        )
      );
    } catch (e) {
      toast.error(apiErrorMsg(e, "Update failed"));
      load();
    }
  };

  const deleteItem = async (sectionId, itemId) => {
    try {
      await api.delete(`/admin/plans/items/${itemId}`);
      setSections((arr) =>
        arr.map((s) => (s.id === sectionId ? { ...s, items: (s.items || []).filter((it) => it.id !== itemId) } : s))
      );
    } catch (e) { toast.error(apiErrorMsg(e, "Delete failed")); }
  };

  const moveItem = async (sectionId, idx, dir) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;
    const items = section.items || [];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[idx];
    const b = items[j];
    const newItems = items.slice();
    [newItems[idx], newItems[j]] = [b, a];
    setSections((arr) => arr.map((s) => (s.id === sectionId ? { ...s, items: newItems } : s)));
    try {
      await Promise.all([
        api.patch(`/admin/plans/items/${a.id}`, { sort_order: b.sort_order }),
        api.patch(`/admin/plans/items/${b.id}`, { sort_order: a.sort_order }),
      ]);
    } catch (e) { toast.error("Reorder failed"); load(); }
  };

  if (!planId) {
    return (
      <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-sm p-4 text-[12.5px] text-[#92400E]" data-testid="plan-sections-empty">
        Save the plan first to start adding dynamic sections (Features, Benefits, Limitations, etc.).
      </div>
    );
  }

  if (loading) return <div className="text-[12.5px] text-[#A1A1AA] py-4">Loading sections…</div>;

  return (
    <div className="space-y-3" data-testid="plan-sections-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#52525B]">Plan Content</div>
          <p className="text-[11.5px] text-[#A1A1AA] mt-0.5">Add unlimited sections — each with its own items, emoji icons, and order.</p>
        </div>
        <button
          type="button"
          onClick={addSection}
          className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#0055FF] hover:bg-[#0044CC] text-white text-[12.5px] font-semibold rounded-sm"
          data-testid="add-section-btn"
        >
          <Plus size={13} /> Add Section
        </button>
      </div>

      {sections.length === 0 && (
        <div className="text-center py-8 border border-dashed border-[#E4E4E7] rounded-sm text-[12.5px] text-[#A1A1AA]" data-testid="plan-sections-zero-state">
          No sections yet — click "Add Section" to start building this plan.
        </div>
      )}

      {sections.map((s, idx) => (
        <SectionCard
          key={s.id}
          idx={idx}
          total={sections.length}
          section={s}
          onUpdate={(patch) => updateSection(s.id, patch)}
          onDelete={() => deleteSection(s.id)}
          onMove={(dir) => moveSection(idx, dir)}
          onAddItem={() => addItem(s.id)}
          onUpdateItem={(itemId, patch) => updateItem(s.id, itemId, patch)}
          onDeleteItem={(itemId) => deleteItem(s.id, itemId)}
          onMoveItem={(itemIdx, dir) => moveItem(s.id, itemIdx, dir)}
        />
      ))}
    </div>
  );
}

function SectionCard({ idx, total, section, onUpdate, onDelete, onMove, onAddItem, onUpdateItem, onDeleteItem, onMoveItem }) {
  const meta = typeMeta(section.type);
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(section.title);

  useEffect(() => { setDraftTitle(section.title); }, [section.title]);

  const commitTitle = () => {
    const v = (draftTitle || "").trim();
    setEditingTitle(false);
    if (v && v !== section.title) onUpdate({ title: v });
    else setDraftTitle(section.title);
  };

  return (
    <div
      className={`border ${section.enabled ? "border-[#E4E4E7]" : "border-dashed border-[#E4E4E7] opacity-70"} bg-white rounded-sm`}
      data-testid={`section-card-${section.id}`}
    >
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-[#F4F4F5]">
        <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${meta.accent}`} data-testid={`section-type-badge-${section.id}`}>
          {meta.label}
        </span>
        {editingTitle ? (
          <input
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => { if (e.key === "Enter") commitTitle(); if (e.key === "Escape") { setDraftTitle(section.title); setEditingTitle(false); } }}
            className="font-heading font-bold text-[15px] flex-1 min-w-0 border-b border-[#0055FF] outline-none px-1 py-0.5 bg-transparent"
            data-testid={`section-title-input-${section.id}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingTitle(true)}
            className="font-heading font-bold text-[15px] flex-1 min-w-0 text-left text-[#09090B] hover:text-[#0055FF] inline-flex items-center gap-1 group"
            data-testid={`section-title-${section.id}`}
          >
            <span className="truncate">{section.title}</span>
            <Pencil size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
        <Select value={section.type} onValueChange={(v) => onUpdate({ type: v })}>
          <SelectTrigger className="rounded-sm h-8 w-[110px] text-[11.5px]" data-testid={`section-type-select-${section.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SECTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5">
          <Switch checked={section.enabled} onCheckedChange={(v) => onUpdate({ enabled: v })} data-testid={`section-enabled-${section.id}`} />
          <span className="text-[11px] text-[#52525B]">On</span>
        </label>
        <div className="inline-flex">
          <button type="button" onClick={() => onMove(-1)} disabled={idx === 0} className="w-7 h-7 inline-flex items-center justify-center text-[#52525B] hover:text-[#0055FF] disabled:opacity-30" data-testid={`section-up-${section.id}`} aria-label="Move up"><ArrowUp size={13} /></button>
          <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1} className="w-7 h-7 inline-flex items-center justify-center text-[#52525B] hover:text-[#0055FF] disabled:opacity-30" data-testid={`section-down-${section.id}`} aria-label="Move down"><ArrowDown size={13} /></button>
        </div>
        <button type="button" onClick={onDelete} className="w-7 h-7 inline-flex items-center justify-center text-[#DC2626] hover:bg-[#FEE2E2] rounded-sm" data-testid={`section-delete-${section.id}`} aria-label="Delete section"><Trash2 size={13} /></button>
      </div>

      <div className="p-3 space-y-1.5">
        {(section.items || []).length === 0 && (
          <div className="text-[11.5px] text-[#A1A1AA] py-2">No items yet.</div>
        )}
        {(section.items || []).map((it, i) => (
          <ItemRow
            key={it.id}
            idx={i}
            total={(section.items || []).length}
            item={it}
            onUpdate={(patch) => onUpdateItem(it.id, patch)}
            onDelete={() => onDeleteItem(it.id)}
            onMove={(dir) => onMoveItem(i, dir)}
          />
        ))}
        <button
          type="button"
          onClick={onAddItem}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#0055FF] hover:text-[#0044CC] mt-2"
          data-testid={`section-add-item-${section.id}`}
        >
          <Plus size={12} /> Add item
        </button>
      </div>
    </div>
  );
}

function ItemRow({ idx, total, item, onUpdate, onDelete, onMove }) {
  const [icon, setIcon] = useState(item.icon || "");
  const [text, setText] = useState(item.text || "");

  useEffect(() => { setIcon(item.icon || ""); setText(item.text || ""); }, [item.icon, item.text]);

  const commitIfChanged = () => {
    const patch = {};
    if ((icon || "") !== (item.icon || "")) patch.icon = icon;
    if ((text || "").trim() !== (item.text || "").trim()) patch.text = text.trim();
    if (Object.keys(patch).length) onUpdate(patch);
  };

  return (
    <div className="flex items-center gap-1.5 group" data-testid={`item-row-${item.id}`}>
      <Input
        value={icon}
        onChange={(e) => setIcon(e.target.value)}
        onBlur={commitIfChanged}
        maxLength={4}
        placeholder="✅"
        className="rounded-sm h-9 w-[60px] text-center text-[15px]"
        data-testid={`item-icon-${item.id}`}
      />
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commitIfChanged}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
        placeholder="Item text…"
        className="rounded-sm h-9 flex-1"
        data-testid={`item-text-${item.id}`}
      />
      <button type="button" onClick={() => onMove(-1)} disabled={idx === 0} className="w-7 h-7 inline-flex items-center justify-center text-[#52525B] hover:text-[#0055FF] disabled:opacity-30" data-testid={`item-up-${item.id}`} aria-label="Move up"><ArrowUp size={12} /></button>
      <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1} className="w-7 h-7 inline-flex items-center justify-center text-[#52525B] hover:text-[#0055FF] disabled:opacity-30" data-testid={`item-down-${item.id}`} aria-label="Move down"><ArrowDown size={12} /></button>
      <button type="button" onClick={onDelete} className="w-7 h-7 inline-flex items-center justify-center text-[#DC2626] hover:bg-[#FEE2E2] rounded-sm" data-testid={`item-delete-${item.id}`} aria-label="Delete item"><Trash2 size={12} /></button>
    </div>
  );
}
